import React, { useState, useEffect, useCallback } from 'react';
import { assetCategoriesApi } from '../../api';
import { AssetCategory, CreateAssetCategoryData, UpdateAssetCategoryData } from '../../types';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import { usePermissions } from '../../hooks/usePermissions';
import './AssetCategories.css';

export function AssetCategories() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('asset_categories.manage');
  
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AssetCategory | null>(null);
  const [formData, setFormData] = useState<CreateAssetCategoryData>({
    name: '',
    code: '',
    description: '',
  });

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await assetCategoriesApi.getCategories({ include_inactive: showInactive });
      setCategories(res.categories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingCategory) {
        await assetCategoriesApi.updateCategory(editingCategory.id, formData as UpdateAssetCategoryData);
      } else {
        await assetCategoriesApi.createCategory(formData);
      }
      await fetchCategories();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      setLoading(true);
      await assetCategoriesApi.deleteCategory(id);
      await fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (category: AssetCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      code: category.code || '',
      description: category.description || '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: '', code: '', description: '' });
  };

  return (
    <div className="asset-categories-page">
      <div className="page-header">
        <div className="page-title">
          <h1>Asset Categories</h1>
          <p>Manage asset categories (Desktop, Laptop, Mobile, etc.)</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowModal(true)}>
            <Icons.Plus /> Add Category
          </Button>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <Card>
        <div className="toolbar">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show Inactive
          </label>
        </div>

        {loading ? (
          <div className="loading-state">Loading categories...</div>
        ) : categories.length === 0 ? (
          <div className="empty-state">
            <Icons.Folder />
            <h3>No categories found</h3>
            <p>Add your first category to get started</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Status</th>
                  {canManage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {categories.map(category => (
                  <tr key={category.id} className={!category.is_active ? 'inactive-row' : ''}>
                    <td className="font-medium">{category.name}</td>
                    <td><code>{category.code || '-'}</code></td>
                    <td>{category.description || '-'}</td>
                    <td>
                      <span className={`status-badge ${category.is_active ? 'active' : 'inactive'}`}>
                        {category.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canManage && (
                      <td>
                        <div className="action-buttons">
                          <button className="icon-button" onClick={() => openEditModal(category)} title="Edit">
                            <Icons.Edit />
                          </button>
                          <button className="icon-button delete-btn" onClick={() => handleDelete(category.id)} title="Delete">
                            <Icons.Trash />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
              <button className="close-button" onClick={resetForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Code</label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., LAPTOP"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={resetForm}>Cancel</Button>
                <Button type="submit" disabled={loading}>{editingCategory ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssetCategories;
